import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import Sidebar from '../components/Sidebar';
import { MdPerson, MdEmail, MdBadge, MdCalendarToday, MdSave, MdImage, MdAdminPanelSettings, MdUpload, MdDelete, MdLogout, MdLock } from 'react-icons/md';
import './Profile.css';

const Profile = () => {
  const { user, setUser, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({ full_name: '', avatar_url: '', job_title: '', username: '', email: '', phone: '', address: '', date_of_birth: '', sex: '', nationality: '' });
  const [pwData, setPwData] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [message, setMessage] = useState({ type: '', text: '' });
  const [pwMessage, setPwMessage] = useState({ type: '', text: '' });
  const [loading, setLoading] = useState(false);
  const [pwLoading, setPwLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);

  useEffect(() => {
    if (user && user.role === 'admin') { navigate('/settings'); return; }
    if (user) {
      setFormData({ full_name: user.full_name || '', avatar_url: user.avatar_url || '', job_title: user.job_title || '', username: user.username || '', email: user.email || '', phone: user.phone || '', address: user.address || '', date_of_birth: user.date_of_birth ? user.date_of_birth.split('T')[0] : '', sex: user.sex || '', nationality: user.nationality || '' });
      setPhotoPreview(user.avatar_url || null);
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const response = await axios.put('/api/users/profile', formData);
      setUser({ ...user, ...response.data });
      setMessage({ type: 'success', text: 'Profile updated successfully!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to update profile.' });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!pwData.current_password || !pwData.new_password || !pwData.confirm_password) {
      setPwMessage({ type: 'error', text: 'All password fields are required.' });
      return;
    }
    if (pwData.new_password.length < 6) {
      setPwMessage({ type: 'error', text: 'New password must be at least 6 characters.' });
      return;
    }
    if (pwData.new_password !== pwData.confirm_password) {
      setPwMessage({ type: 'error', text: 'New passwords do not match.' });
      return;
    }
    setPwLoading(true);
    setPwMessage({ type: '', text: '' });
    try {
      await axios.put('/api/users/profile', { current_password: pwData.current_password, new_password: pwData.new_password });
      setPwMessage({ type: 'success', text: 'Password changed successfully!' });
      setPwData({ current_password: '', new_password: '', confirm_password: '' });
      setTimeout(() => setPwMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setPwMessage({ type: 'error', text: error.response?.data?.error || 'Failed to change password.' });
    } finally {
      setPwLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { setMessage({ type: 'error', text: 'Please select a valid image file.' }); return; }
    if (file.size > 5 * 1024 * 1024) { setMessage({ type: 'error', text: 'Image size must be less than 5MB.' }); return; }
    setUploadingPhoto(true);
    try {
      const uploadData = new FormData();
      uploadData.append('photo', file);
      const response = await axios.post('/api/users/upload-photo', uploadData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const newAvatarUrl = response.data.avatar_url;
      setFormData({ ...formData, avatar_url: newAvatarUrl });
      setPhotoPreview(newAvatarUrl);
      setUser({ ...user, avatar_url: newAvatarUrl });
      setMessage({ type: 'success', text: 'Photo uploaded successfully!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to upload photo. Please try again.' });
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleRemovePhoto = () => { setFormData({ ...formData, avatar_url: '' }); setPhotoPreview(null); };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return '#EF4444';
      case 'manager': return '#F59E0B';
      case 'member': return '#10B981';
      default: return '#6B7280';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const getProfileCompletion = () => {
    let completed = 0;
    if (user?.username) completed++;
    if (user?.email) completed++;
    if (user?.full_name) completed++;
    if (user?.avatar_url) completed++;
    if (user?.job_title) completed++;
    return Math.round((completed / 5) * 100);
  };

  return (
    <div className="app-container">
      <Sidebar />
      <div className="main-content profile-main">
        <div className="profile-page">
          <div className="profile-header">
            <h1>My Profile</h1>
            <p className="profile-subtitle">Manage your personal information</p>
          </div>

          <div className="profile-grid">
            {/* Left column */}
            <div className="profile-left-col">
              {/* Personal Info Card */}
              <div className="profile-card">
                <div className="card-header">
                  <div className="card-icon"><MdPerson /></div>
                  <div>
                    <h2>Personal Information</h2>
                    <p className="card-description">Update your profile details</p>
                  </div>
                </div>
                <form onSubmit={handleSubmit}>
                  {message.text && <div className={`message ${message.type}`}>{message.text}</div>}
                  <div className="form-group">
                    <label><MdBadge className="label-icon" />Username</label>
                    <input type="text" value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })} placeholder="Enter your username" />
                  </div>
                  <div className="form-group">
                    <label><MdEmail className="label-icon" />Email</label>
                    <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="Enter your email" />
                  </div>
                  <div className="form-group">
                    <label><MdPerson className="label-icon" />Phone Number</label>
                    <input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="e.g. +251 917 397 648" />
                  </div>
                  <div className="form-group">
                    <label><MdPerson className="label-icon" />Address</label>
                    <input type="text" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} placeholder="e.g. Addis Ababa, Ethiopia" />
                  </div>
                  <div className="form-group">
                    <label><MdCalendarToday className="label-icon" />Date of Birth</label>
                    <input type="date" value={formData.date_of_birth} onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label><MdPerson className="label-icon" />Sex</label>
                    <select value={formData.sex} onChange={(e) => setFormData({ ...formData, sex: e.target.value })} style={{ width: '100%', padding: '10px 12px', border: '2px solid #E5E7EB', borderRadius: '8px', fontSize: '14px', color: '#1F2937', background: 'white' }}>
                      <option value="">— Select —</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label><MdPerson className="label-icon" />Nationality</label>
                    <input type="text" value={formData.nationality} onChange={(e) => setFormData({ ...formData, nationality: e.target.value })} placeholder="e.g. Ethiopian" />
                  </div>
                  <div className="form-group">
                    <label><MdPerson className="label-icon" />Full Name</label>
                    <input type="text" value={formData.full_name} onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} placeholder="Enter your full name" />
                  </div>
                  <div className="form-group">
                    <label><MdBadge className="label-icon" />Job Title</label>
                    <input type="text" value={formData.job_title} disabled placeholder="Only admins can change job titles" />
                    <small><MdBadge className="small-icon" />Only admins can change job titles</small>
                  </div>
                  <div className="form-group">
                    <label><MdImage className="label-icon" />Profile Photo</label>
                    {photoPreview && (
                      <div className="photo-preview-container">
                        <img src={photoPreview} alt="Profile preview" className="photo-preview-large" onError={(e) => e.target.style.display = 'none'} />
                        <button type="button" className="btn-remove-photo" onClick={handleRemovePhoto}><MdDelete /></button>
                      </div>
                    )}
                    <div className="photo-upload-section">
                      <input type="file" id="photo-upload" accept="image/*" onChange={handlePhotoUpload} style={{ display: 'none' }} disabled={uploadingPhoto} />
                      <label htmlFor="photo-upload" className={`btn-upload-photo ${uploadingPhoto ? 'uploading' : ''}`}>
                        <MdUpload />{uploadingPhoto ? 'Uploading...' : 'Upload Photo'}
                      </label>
                      <small><MdImage className="small-icon" />Max size: 5MB. JPG, PNG, GIF</small>
                    </div>
                  </div>
                  <div className="form-group">
                    <label><MdImage className="label-icon" />Or Enter Avatar URL</label>
                    <input type="url" value={formData.avatar_url} onChange={(e) => { setFormData({ ...formData, avatar_url: e.target.value }); setPhotoPreview(e.target.value); }} placeholder="https://example.com/avatar.jpg" />
                  </div>
                  <button type="submit" className="btn-save" disabled={loading}>
                    <MdSave />{loading ? 'Saving...' : 'Save Changes'}
                  </button>
                </form>
              </div>

              {/* Change Password Card */}
              <div className="profile-card">
                <div className="card-header">
                  <div className="card-icon" style={{ background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)' }}><MdLock /></div>
                  <div>
                    <h2>Change Password</h2>
                    <p className="card-description">Update your account password</p>
                  </div>
                </div>
                <form onSubmit={handlePasswordSubmit}>
                  {pwMessage.text && <div className={`message ${pwMessage.type}`}>{pwMessage.text}</div>}
                  <div className="form-group">
                    <label>Current Password</label>
                    <input type="password" value={pwData.current_password} onChange={(e) => setPwData({ ...pwData, current_password: e.target.value })} placeholder="Enter current password" />
                  </div>
                  <div className="form-group">
                    <label>New Password</label>
                    <input type="password" value={pwData.new_password} onChange={(e) => setPwData({ ...pwData, new_password: e.target.value })} placeholder="Min 6 characters" />
                  </div>
                  <div className="form-group">
                    <label>Confirm New Password</label>
                    <input type="password" value={pwData.confirm_password} onChange={(e) => setPwData({ ...pwData, confirm_password: e.target.value })} placeholder="Confirm new password" />
                  </div>
                  <button type="submit" className="btn-save" disabled={pwLoading}>
                    <MdLock />{pwLoading ? 'Updating...' : 'Update Password'}
                  </button>
                </form>
              </div>
            </div>

            {/* Right sidebar */}
            <div className="profile-sidebar">
              <div className="profile-card account-card">
                <div className="card-header">
                  <div className="card-icon"><MdAdminPanelSettings /></div>
                  <div><h2>Account Info</h2></div>
                </div>
                <div className="info-list">
                  <div className="info-item">
                    <div className="info-icon" style={{ background: `${getRoleColor(user?.role)}15`, color: getRoleColor(user?.role), border: `2px solid ${getRoleColor(user?.role)}30` }}>
                      <MdAdminPanelSettings />
                    </div>
                    <div className="info-content">
                      <span className="info-label">Role</span>
                      <span className="info-value">{user?.role || 'N/A'}</span>
                    </div>
                  </div>
                  {user?.job_title && (
                    <div className="info-item">
                      <div className="info-icon" style={{ background: '#D1FAE5', color: '#059669', border: '2px solid #10B981' }}><MdBadge /></div>
                      <div className="info-content">
                        <span className="info-label">Job Title</span>
                        <span className="info-value">{user.job_title}</span>
                      </div>
                    </div>
                  )}
                  <div className="info-item">
                    <div className="info-icon" style={{ background: '#DBEAFE', color: '#1E40AF', border: '2px solid #3B82F6' }}><MdCalendarToday /></div>
                    <div className="info-content">
                      <span className="info-label">Member Since</span>
                      <span className="info-value">{formatDate(user?.created_at)}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="profile-card stats-card">
                <h3>Profile Completion</h3>
                <div className="stats-list">
                  <div className="stat-item">
                    <span className="stat-label">Complete your profile</span>
                    <div className="progress-bar">
                      <div className="progress-fill" style={{ width: `${getProfileCompletion()}%` }}></div>
                    </div>
                    <span className="stat-value">{getProfileCompletion()}%</span>
                  </div>
                </div>
              </div>

              <div className="profile-card logout-card">
                <button onClick={() => { logout(); navigate('/login'); }} className="btn-logout-mobile">
                  <MdLogout />Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;

